import { Milliseconds } from '../../constants'
import { PotentialObjectType } from '../../types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../lambda'
import { daysInLastUTCMonth } from '../time'
import { Dayjs } from 'dayjs'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export enum S3TimeKeyLevel {
  YEAR,
  MONTH,
  DAY,
  HOUR,
  MINUTE,
  SECOND,
  MILLISECOND,
}

export enum S3TimeKeyStrategy {
  OBJECT_TIME = 'OBJECT_TIME',
  TIME_OBJECT = 'TIME_OBJECT',
}

export type S3TimeKeyPrefixRequest = {
  timestamp?: number
  s3TimeKeyLevel?: S3TimeKeyLevel
}

export type S3TimeKeyRequest = S3TimeKeyPrefixRequest & {
  object: PotentialObjectType
  s3TimeKeyStrategy?: S3TimeKeyStrategy
}

export type S3TimeKeyElementsType = [y: string, m: string, d: string, h: string, min: string, s: string, ms: string]

export type S3PartialTimeKeyElementsType = [
  y: string | undefined,
  m: string | undefined,
  d: string | undefined,
  h: string | undefined,
  min: string | undefined,
  s: string | undefined,
  ms: string | undefined,
]

const S3FolderSeperator = '/'

/**
 * pad single digits to length 2 with leading zero
 */
const psd = (x: number): string => x.toString().padStart(2, '0')

/**
 * pad single digits to length 3 with leading zero
 */
const psd3 = (x: number): string => x.toString().padStart(3, '0')

/** returns array of date elements in form [ y, m, d, h, min, s, ms ] */
export const getS3TimeKeyElements = (date: Date): S3TimeKeyElementsType => {
  const [y, m, d, h, min, s, ms] = [
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  ]
  const timeKeyElements: S3TimeKeyElementsType = [y.toString(), psd(m), psd(d), psd(h), psd(min), psd(s), psd3(ms)]
  return timeKeyElements
}

export const getS3TimeKeyPrefix = ({ timestamp, s3TimeKeyLevel = S3TimeKeyLevel.DAY }: S3TimeKeyPrefixRequest) => {
  const date = timestamp ? new Date(timestamp) : new Date()
  return getS3TimeKeyElements(date)
    .slice(0, s3TimeKeyLevel + 1)
    .join(S3FolderSeperator)
}

export const getS3TimeKey = ({
  object,
  timestamp,
  s3TimeKeyLevel = S3TimeKeyLevel.DAY,
  s3TimeKeyStrategy = S3TimeKeyStrategy.OBJECT_TIME,
}: S3TimeKeyRequest) => {
  const timeKey = getS3TimeKeyPrefix({ timestamp, s3TimeKeyLevel })
  const objectKey = [object.type, object.id].join(S3FolderSeperator)

  return s3TimeKeyStrategy === S3TimeKeyStrategy.OBJECT_TIME ? objectKey + S3FolderSeperator + timeKey : timeKey + S3FolderSeperator + objectKey
}

export const getS3TimeKeys = (s3TimeLevel: S3TimeKeyLevel, startIn: Dayjs, endIn: Dayjs) => {
  const start: S3TimeKeyElementsType = getS3TimeKeyElements(new Date(startIn.unix() * Milliseconds.SECOND))
  const end: S3TimeKeyElementsType = getS3TimeKeyElements(new Date(endIn.unix() * Milliseconds.SECOND))
  return incrementFromTo({ start, end, level: s3TimeLevel, inc: 1 })
}

// ignoring days in month, days and months need to include basis and start from 1
const bN: [y: 3000, m: 13, d: 32, h: 24, min: 60, s: 60, ms: 1000] = [3000, 13, 32, 24, 60, 60, 1000]

const b0: [y: 0, m: 1, d: 1, h: 0, min: 0, s: 0, ms: 0] = [0, 1, 1, 0, 0, 0, 0]

const getDaysInMonth = (keyIn: S3TimeKeyElementsType) => {
  const date = new Date(Date.UTC(parseInt(keyIn[0]), parseInt(keyIn[1])))
  const daysInMonth = daysInLastUTCMonth(date.valueOf())
  debug && console.log(`days in month ${keyIn[2]}: ${daysInMonth}`, date.toString())
  return daysInMonth
}

export const getCommonLevel = (start: S3TimeKeyElementsType, end: S3TimeKeyElementsType): S3TimeKeyLevel | undefined => {
  let commonLevel: S3TimeKeyLevel | undefined = undefined
  for (let [i, startEl] of start.entries()) {
    const endEl = end[i]
    if (startEl === endEl) {
      commonLevel = i
    } else {
      break // must break otherwise can match on lower order fields
    }
  }
  return commonLevel
}

/**
 *
 * @param keyIn the full key to increment
 * @param level the level at which increments should happen
 * @param inc the number of increments at the level to increment by. This is NOT millisconds, it is the number of units of level
 * @returns
 */
export const incrementS3TimeKey = (keyIn: S3TimeKeyElementsType, level: S3TimeKeyLevel, inc: number = 1): S3TimeKeyElementsType => {
  const key: S3TimeKeyElementsType = [...keyIn] // spread otherwise will mutate
  const xnp = parseInt(key[level]) + inc // raw accumated value
  const max = level === S3TimeKeyLevel.DAY ? getDaysInMonth(keyIn) + 1 : bN[level] // when to rollover, at max div should be > 0, mod should be 0 or more
  const div = Math.floor(xnp / max) // dividend
  const mod = xnp % max // modulus
  const accumulation = b0[level] === 1 && div > 0 ? b0[level] + mod : mod // if rollover ( a > 0 ) and either Month or Day, start at 1

  debug && console.log({ level, parsedInt: parseInt(key[level]), key, max, xnp, a: div, b: mod })
  key[level] = level === S3TimeKeyLevel.MILLISECOND ? psd3(accumulation) : psd(accumulation)
  if (div > 0) {
    debug && console.log(`incremeting ${level - 1}, by ${div}`)
    return incrementS3TimeKey(key, level - 1, div)
  } else {
    return key
  }
}

type IncrementS3KeyFromToParams = { start: S3TimeKeyElementsType; end: S3TimeKeyElementsType; level: S3TimeKeyLevel; inc: number }

export const incrementFromTo = ({ start, end, level, inc = 1 }: IncrementS3KeyFromToParams) => {
  const keys: S3TimeKeyElementsType[] = [start]
  let key = start
  let isBetween: boolean = true
  debug && console.log({ start, end, level, inc })
  do {
    key = incrementS3TimeKey(key, level, inc)
    isBetween = key.join() <= end.join()
    keys.push(key)
    debug && console.log({ isBetween, key, end })
  } while (isBetween)
  return keys
}

export const s3TimeKeyToDate = (s3TimeKey: S3PartialTimeKeyElementsType) => {
  const diff = 7 - s3TimeKey.length
  for (let i = 0; i < diff; i++) s3TimeKey.push('00')
  const [y, m, d, h, min, s, ms] = s3TimeKey.map((x) => (x ? parseInt(x) : 0))
  const date = new Date()
  date.setUTCFullYear(y)
  date.setUTCMonth(m - 1)
  date.setUTCDate(d)
  date.setUTCHours(h)
  date.setUTCMinutes(min)
  date.setUTCSeconds(s)
  date.setUTCMilliseconds(ms)
  return date
}
